import { useEffect, useState } from "react";
import { Settings, Building2, Clock, Mail, AlertTriangle, Download } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_DASHBOARD } from "@/constants/testIds";
import { toast } from "sonner";

export default function CompanyProfile() {
  const { user, refreshUser } = useAuth();
  const [workWeek, setWorkWeek] = useState(user?.work_week || "mon_fri");
  const [expectedCheckinTime, setExpectedCheckinTime] = useState("");
  const [expectedCheckoutTime, setExpectedCheckoutTime] = useState("");
  const [toleranceMinutes, setToleranceMinutes] = useState("");
  const [weeklyReportEmail, setWeeklyReportEmail] = useState("");
  const [weeklyReportDay, setWeeklyReportDay] = useState("");
  const [payrollPeriodicity, setPayrollPeriodicity] = useState("");
  const [payrollCycleStartDay, setPayrollCycleStartDay] = useState("");
  const [lateArrivalsThreshold, setLateArrivalsThreshold] = useState("");
  const [companyProfile, setCompanyProfile] = useState({ razon_social: "", rfc: "", domicilio: "", representante_legal: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    api.get("/onboarding/status").then(({ data }) => {
      setCompanyProfile({
        razon_social: data.razon_social || "", rfc: data.rfc || "",
        domicilio: data.domicilio || "", representante_legal: data.representante_legal || "",
      });
      setExpectedCheckinTime(data.expected_checkin_time || "");
      setExpectedCheckoutTime(data.expected_checkout_time || "");
      setToleranceMinutes(data.expected_checkin_tolerance_minutes ?? "");
      setWeeklyReportEmail(data.weekly_report_email || "");
      setWeeklyReportDay(data.weekly_report_day || "");
      setPayrollPeriodicity(data.payroll_periodicity || "");
      setPayrollCycleStartDay(data.payroll_cycle_start_day || "");
      setLateArrivalsThreshold(data.late_arrivals_threshold ?? "");
    });
  }, []);

  useEffect(() => {
    if (user?.work_week) setWorkWeek(user.work_week);
  }, [user]);

  const saveSetting = async (payload, successMessage, errorMessage) => {
    try {
      await api.patch("/onboarding/settings", payload);
      toast.success(successMessage);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || errorMessage);
    }
  };

  const saveWorkWeek = async (value) => {
    setWorkWeek(value);
    await saveSetting({ work_week: value }, "Días laborables actualizados", "No se pudo actualizar la configuración");
    await refreshUser();
  };

  const saveExpectedCheckinTime = async (value) => {
    setExpectedCheckinTime(value);
    await saveSetting({ expected_checkin_time: value }, "Horario de entrada actualizado", "No se pudo actualizar el horario de entrada");
  };

  const saveExpectedCheckoutTime = async (value) => {
    setExpectedCheckoutTime(value);
    await saveSetting({ expected_checkout_time: value }, "Horario de salida actualizado", "No se pudo actualizar el horario de salida");
  };

  const saveTolerance = async () => {
    const value = toleranceMinutes === "" ? null : parseInt(toleranceMinutes, 10);
    await saveSetting({ expected_checkin_tolerance_minutes: value ?? 0 }, "Tolerancia actualizada", "No se pudo actualizar la tolerancia");
  };

  const saveWeeklyReportEmail = async () => {
    await saveSetting({ weekly_report_email: weeklyReportEmail }, "Correo del reporte semanal actualizado", "No se pudo actualizar el correo");
  };

  const saveWeeklyReportDay = async (value) => {
    setWeeklyReportDay(value);
    await saveSetting({ weekly_report_day: value }, "Día de envío actualizado", "No se pudo actualizar el día de envío");
  };

  const savePayrollPeriodicity = async (value) => {
    setPayrollPeriodicity(value);
    await saveSetting({ payroll_periodicity: value }, "Periodicidad de nómina actualizada", "No se pudo actualizar la periodicidad");
  };

  const savePayrollCycleStartDay = async (value) => {
    setPayrollCycleStartDay(value);
    await saveSetting({ payroll_cycle_start_day: value }, "Día de inicio de ciclo actualizado", "No se pudo actualizar el día de inicio");
  };

  const saveLateArrivalsThreshold = async () => {
    const value = lateArrivalsThreshold === "" ? null : parseInt(lateArrivalsThreshold, 10);
    if (value === null) return;
    await saveSetting({ late_arrivals_threshold: value }, "Umbral de retardos actualizado", "No se pudo actualizar el umbral");
  };

  const [exporting, setExporting] = useState(false);

  const exportCompanyData = async () => {
    setExporting(true);
    try {
      const response = await api.get("/export/company-data", { responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "cerh-export.json";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Exportación descargada");
    } catch (err) {
      toast.error("No se pudo generar la exportación");
    } finally {
      setExporting(false);
    }
  };

  const saveCompanyProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/onboarding/company-profile", companyProfile);
      toast.success("Perfil de empresa actualizado");
    } catch (err) {
      toast.error("No se pudo actualizar el perfil de empresa");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-md">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
          <Building2 className="w-7 h-7 text-petroleum" strokeWidth={1.5} /> Perfil de Empresa
        </h1>
        <p className="text-charcoal/60 mt-1">Configuración y datos legales que se usan para autocompletar documentos.</p>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Configuración de la empresa</h2>
        <div className="bg-card border border-petroleum/10 rounded-xl p-5 space-y-5">
          <div className="flex items-center gap-4">
            <Settings className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <Label className="text-sm text-charcoal/80 block mb-1">Días laborables</Label>
              <select data-testid="dashboard-work-week-select" value={workWeek} onChange={(e) => saveWorkWeek(e.target.value)} className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option value="mon_fri">Lunes a Viernes</option>
                <option value="mon_sat">Lunes a Sábado</option>
                <option value="mon_sun">Lunes a Domingo</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Clock className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <Label className="text-sm text-charcoal/80 block mb-1">Horario general de entrada y salida esperados</Label>
              <p className="text-xs text-charcoal/50 mb-1.5">Se usan para avisarte en el Dashboard y en el Reporte Semanal si un empleado no ha marcado su entrada. Déjalos vacíos para desactivar la alerta.</p>
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-xs text-charcoal/60 block mb-1">Entrada</Label>
                  <Input data-testid={ADMIN_DASHBOARD.expectedCheckinTimeInput} type="time" value={expectedCheckinTime} onChange={(e) => saveExpectedCheckinTime(e.target.value)} className="max-w-[140px]" />
                </div>
                <div>
                  <Label className="text-xs text-charcoal/60 block mb-1">Salida</Label>
                  <Input data-testid={ADMIN_DASHBOARD.expectedCheckoutTimeInput} type="time" value={expectedCheckoutTime} onChange={(e) => saveExpectedCheckoutTime(e.target.value)} className="max-w-[140px]" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-charcoal/60 block mb-1">Tolerancia antes de contar retardo/falta (minutos)</Label>
                <Input
                  data-testid={ADMIN_DASHBOARD.expectedCheckinToleranceInput}
                  type="number" min="0" max="120" placeholder="0"
                  value={toleranceMinutes}
                  onChange={(e) => setToleranceMinutes(e.target.value)}
                  onBlur={saveTolerance}
                  className="max-w-[100px]"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Mail className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <Label className="text-sm text-charcoal/80 block mb-1">Reporte semanal de asistencia por correo</Label>
              <p className="text-xs text-charcoal/50 mb-1.5">Opcional. Si dejas el correo vacío, el reporte sigue disponible para descarga manual en Registro de Jornada, sin envío automático.</p>
              <div className="flex items-center gap-3">
                <Input
                  data-testid={ADMIN_DASHBOARD.weeklyReportEmailInput}
                  type="email" placeholder="rh@tuempresa.mx"
                  value={weeklyReportEmail}
                  onChange={(e) => setWeeklyReportEmail(e.target.value)}
                  onBlur={saveWeeklyReportEmail}
                  className="flex-1"
                />
                <select
                  data-testid={ADMIN_DASHBOARD.weeklyReportDaySelect}
                  value={weeklyReportDay}
                  onChange={(e) => saveWeeklyReportDay(e.target.value)}
                  className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
                >
                  <option value="">Sin envío automático</option>
                  <option value="monday">Lunes</option>
                  <option value="tuesday">Martes</option>
                  <option value="wednesday">Miércoles</option>
                  <option value="thursday">Jueves</option>
                  <option value="friday">Viernes</option>
                  <option value="saturday">Sábado</option>
                  <option value="sunday">Domingo</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Falta por exceso de retardos</h2>
        <div className="bg-card border border-petroleum/10 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-4">
            <AlertTriangle className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <Label className="text-sm text-charcoal/80 block mb-1">Periodicidad de nómina</Label>
              <p className="text-xs text-charcoal/50 mb-1.5">Se usa para saber cuál es el "periodo actual" al contar retardos. Quincenal y mensual siguen el calendario estándar (1-15/16-fin de mes); semanal necesita que elijas el día en que inicia tu ciclo.</p>
              <div className="flex items-center gap-3">
                <select data-testid={ADMIN_DASHBOARD.payrollPeriodicitySelect} value={payrollPeriodicity} onChange={(e) => savePayrollPeriodicity(e.target.value)} className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                  <option value="">Sin configurar</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
                {payrollPeriodicity === "semanal" && (
                  <select data-testid={ADMIN_DASHBOARD.payrollCycleStartDaySelect} value={payrollCycleStartDay} onChange={(e) => savePayrollCycleStartDay(e.target.value)} className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                    <option value="">Día de inicio...</option>
                    <option value="monday">Lunes</option>
                    <option value="tuesday">Martes</option>
                    <option value="wednesday">Miércoles</option>
                    <option value="thursday">Jueves</option>
                    <option value="friday">Viernes</option>
                    <option value="saturday">Sábado</option>
                    <option value="sunday">Domingo</option>
                  </select>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <AlertTriangle className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <Label className="text-sm text-charcoal/80 block mb-1">Número de retardos que constituye una falta</Label>
              <p className="text-xs text-charcoal/50 mb-1.5">Al alcanzar este número de retardos dentro del periodo actual, verás una notificación pendiente en Registro de Jornada para revisar y confirmar — nunca se crea la falta automáticamente sin tu aprobación.</p>
              <Input
                data-testid={ADMIN_DASHBOARD.lateArrivalsThresholdInput}
                type="number" min="1" max="30" placeholder="Ej. 3"
                value={lateArrivalsThreshold}
                onChange={(e) => setLateArrivalsThreshold(e.target.value)}
                onBlur={saveLateArrivalsThreshold}
                className="max-w-[100px]"
              />
            </div>
          </div>
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Datos legales de la empresa</h2>
        <div className="bg-card border border-petroleum/10 rounded-xl p-5 space-y-3">
          <p className="text-xs text-charcoal/50">Se usan para autocompletar los documentos que generes en la Biblioteca de Documentos. El correo de contacto es el mismo con el que iniciaste sesión ({user?.email}).</p>
          <div>
            <Label className="text-xs">Razón social</Label>
            <Input data-testid={ADMIN_DASHBOARD.razonSocialInput} value={companyProfile.razon_social} onChange={(e) => setCompanyProfile((p) => ({ ...p, razon_social: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">RFC</Label>
            <Input data-testid={ADMIN_DASHBOARD.rfcInput} value={companyProfile.rfc} onChange={(e) => setCompanyProfile((p) => ({ ...p, rfc: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Domicilio completo</Label>
            <Input data-testid={ADMIN_DASHBOARD.domicilioInput} value={companyProfile.domicilio} onChange={(e) => setCompanyProfile((p) => ({ ...p, domicilio: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Representante legal</Label>
            <Input data-testid={ADMIN_DASHBOARD.representanteLegalInput} value={companyProfile.representante_legal} onChange={(e) => setCompanyProfile((p) => ({ ...p, representante_legal: e.target.value }))} />
          </div>
          <Button data-testid={ADMIN_DASHBOARD.saveCompanyProfileButton} disabled={savingProfile} onClick={saveCompanyProfile} size="sm" className="bg-petroleum rounded-full">
            {savingProfile ? "Guardando..." : "Guardar"}
          </Button>
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Tus datos</h2>
        <div className="bg-card border border-petroleum/10 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <Download className="w-5 h-5 text-petroleum shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-sm text-charcoal/80 mb-1">Exportar una copia completa de tus datos</p>
              <p className="text-xs text-charcoal/50 mb-3">Descarga en un solo archivo todo lo que tu empresa tiene cargado en CERH: expedientes, políticas, vacaciones, incidencias, jornada y más. Úsalo cuando quieras, no solo antes de cancelar tu cuenta.</p>
              <Button data-testid="company-profile-export-data-button" disabled={exporting} onClick={exportCompanyData} size="sm" variant="outline" className="rounded-full">
                {exporting ? "Generando..." : "Exportar mis datos"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
